import { useCallback, useEffect, useRef, useState } from 'react';

import { PaginatedList } from 'utils/api';
import { PICKER_PAGE_SIZE, PICKER_SEARCH_DEBOUNCE_MS } from 'utils/constants';

interface FetchParams {
  name?: string;
  page: number;
  size: number;
  signal?: AbortSignal;
}

interface UsePaginatedOptionsConfig<T> {
  fetch: (params: FetchParams) => Promise<PaginatedList<T[]>>;
  pageSize?: number;
  enabled?: boolean;
  searchDebounceMs?: number;
}

interface UsePaginatedOptionsResult<T> {
  options: T[];
  hasMore: boolean;
  loading: boolean;
  error: Error | null;
  search: string;
  setSearch: (q: string) => void;
  loadMore: () => void;
  retry: () => void;
  refresh: () => Promise<void>;
}

export function usePaginatedOptions<T>({
  fetch,
  pageSize = PICKER_PAGE_SIZE,
  enabled = true,
  searchDebounceMs = PICKER_SEARCH_DEBOUNCE_MS,
}: UsePaginatedOptionsConfig<T>): UsePaginatedOptionsResult<T> {
  const [options, setOptions] = useState<T[]>([]);
  const [nextPage, setNextPage] = useState(0);
  const [total, setTotal] = useState<number | null>(null);
  const [search, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const abortRef = useRef<AbortController | null>(null);
  const fetchRef = useRef(fetch);
  fetchRef.current = fetch;

  useEffect(() => {
    if (search === debouncedSearch) return;
    const handle = window.setTimeout(() => setDebouncedSearch(search), searchDebounceMs);
    return () => window.clearTimeout(handle);
  }, [search, debouncedSearch, searchDebounceMs]);

  const runFetch = useCallback(
    async (page: number, name: string, append: boolean) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setLoading(true);
      setError(null);
      try {
        const result = await fetchRef.current({
          name: name || undefined,
          page,
          size: pageSize,
          signal: controller.signal,
        });
        if (controller.signal.aborted) return;
        setOptions((prev) => (append ? [...prev, ...result.content] : result.content));
        setTotal(result.totalElements);
        setNextPage(page + 1);
      } catch (e) {
        if (controller.signal.aborted) return;
        setError(e as Error);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    },
    [pageSize],
  );

  useEffect(() => {
    if (!enabled) return;
    setOptions([]);
    setNextPage(0);
    setTotal(null);
    runFetch(0, debouncedSearch, false);
    return () => {
      abortRef.current?.abort();
    };
  }, [enabled, debouncedSearch, runFetch, fetch]);

  const accumulated = options.length;
  const hasMore = total === null ? false : accumulated < total;

  const loadMore = useCallback(() => {
    if (loading || !hasMore) return;
    runFetch(nextPage, debouncedSearch, true);
  }, [loading, hasMore, nextPage, debouncedSearch, runFetch]);

  const retry = useCallback(() => {
    if (loading) return;
    const append = nextPage > 0;
    runFetch(nextPage, debouncedSearch, append);
  }, [loading, nextPage, debouncedSearch, runFetch]);

  const refresh = useCallback(async () => {
    await runFetch(0, debouncedSearch, false);
  }, [debouncedSearch, runFetch]);

  return {
    options,
    hasMore,
    loading,
    error,
    search,
    setSearch: setSearchInput,
    loadMore,
    retry,
    refresh,
  };
}
